import { useState, useEffect, useCallback } from "react";
import { saveToIndexedDB, loadFromIndexedDB } from "../../../util/indexedDBUtils";

/**
 * useSubstepPersistence - Custom hook for managing substep form data persistence
 * 
 * Handles:
 * - Restoring data from localStorage (with legacy key support)
 * - Restoring data from IndexedDB as fallback
 * - Auto-saving to IndexedDB
 * - Clearing storage after successful save
 * 
 * @param {Object} config
 * @param {string} config.mode - 'add' or 'edit'
 * @param {string} config.substepId - Substep ID (for edit mode)
 * @param {string} config.shortDescription - Current short description
 * @param {function} config.setShortDescription - Setter for short description
 * 
 * @returns {Object} Persistence state and handlers
 */
const useSubstepPersistence = ({
  mode,
  substepId,
  shortDescription,
  setShortDescription,
}) => {
  const [localStorageRestored, setLocalStorageRestored] = useState(false);
  const [hasLocalStorageData, setHasLocalStorageData] = useState(false);

  /**
   * Get localStorage key with support for legacy keys
   */
  const getWithLegacyFallback = useCallback((camelKey, legacyKey) => {
    const v = localStorage.getItem(camelKey);
    if (v !== null && v !== undefined) return v;
    return localStorage.getItem(legacyKey);
  }, []);

  /**
   * Restore data from localStorage (edit mode)
   */
  useEffect(() => {
    if (mode !== "edit") return;

    const savedShortDescription = getWithLegacyFallback("editSubstepShortDescription", "editsubstepShortDescription");

    const isReturningFromPreview = savedShortDescription && savedShortDescription !== "undefined";

    if (isReturningFromPreview) {
      setHasLocalStorageData(true);

      // Restore short description
      if (savedShortDescription && savedShortDescription !== "undefined") {
        setShortDescription(savedShortDescription);
      }

      // Clear localStorage after restoration
      [
        "editSubstepShortDescription",
        // Legacy keys
        "editsubstepShortDescription",
      ].forEach((k) => localStorage.removeItem(k));
    } else {
      // Fallback: restore from IndexedDB
      const restoreFromIndexedDB = async () => {
        try {
          const draftId = `substep:${substepId || "new"}`;
          const record = await loadFromIndexedDB("substepDrafts", draftId);
          if (record && record.data) {
            const d = record.data;
            if (d.shortDescription) setShortDescription(d.shortDescription);
          }
        } catch (e) {
          console.warn("IndexedDB restore failed:", e?.message || e);
        } finally {
          setHasLocalStorageData(false);
          setLocalStorageRestored(true);
        }
      };
      restoreFromIndexedDB();
      return;
    }

    setLocalStorageRestored(true);
  }, [mode, getWithLegacyFallback, substepId]);

  /**
   * Restore data from localStorage (add mode)
   */
  useEffect(() => {
    if (mode !== "add") return;

    const savedShortDescription = localStorage.getItem("substepShortDescription");

    const isReturningFromPreview = savedShortDescription && savedShortDescription !== "undefined";

    if (isReturningFromPreview) {
      // Restore short description
      if (savedShortDescription && savedShortDescription !== "undefined") {
        setShortDescription(savedShortDescription);
      }

      // Clear localStorage after restoration
      [
        "substepShortDescription",
      ].forEach((k) => localStorage.removeItem(k));
    }

    setLocalStorageRestored(true);
  }, [mode]);

  /**
   * Auto-save to IndexedDB when data changes
   */
  useEffect(() => {
    if (!localStorageRestored) return;

    const draftId = `substep:${substepId || "new"}`;
    const payload = {
      shortDescription,
      updatedAt: Date.now(),
      mode,
    };

    saveToIndexedDB("substepDrafts", draftId, payload).catch((err) => {
      console.warn("IndexedDB save failed:", err);
    });
  }, [
    localStorageRestored,
    shortDescription,
    mode,
    substepId,
  ]);

  /**
   * Save data to localStorage for preview navigation
   */
  const saveForPreview = () => {
    const getKey = (base) => {
      return mode === "edit" ? `edit${base}` : `${base.charAt(0).toLowerCase()}${base.slice(1)}`;
    };

    try {
      localStorage.setItem(getKey("SubstepShortDescription"), shortDescription);

      // Backward compatibility: also write legacy lowercase edit-* keys
      if (mode === "edit") {
        localStorage.setItem("editsubstepShortDescription", shortDescription);
      }
    } catch (e) {
      console.warn("Failed to persist substep draft for preview:", e);
    }
  };

  /**
   * Clear all stored data (after successful save)
   */
  const clearStoredData = () => {
    const draftId = `substep:${substepId || "new"}`;
    
    // Clear IndexedDB
    try {
      const request = indexedDB.open("canvasElementsDB");
      request.onsuccess = () => {
        const db = request.result;
        if (db.objectStoreNames.contains("substepDrafts")) {
          const transaction = db.transaction(["substepDrafts"], "readwrite");
          const store = transaction.objectStore("substepDrafts");
          store.delete(draftId);
        }
      };
    } catch (e) {
      console.warn("Failed to clear IndexedDB:", e);
    }

    // Clear localStorage
    const keys = mode === "edit"
      ? [
          "editSubstepShortDescription",
          // Legacy keys
          "editsubstepShortDescription",
        ]
      : [
          "substepShortDescription",
        ];

    keys.forEach((k) => localStorage.removeItem(k));
  };

  return {
    localStorageRestored,
    hasLocalStorageData,
    saveForPreview,
    clearStoredData,
  };
};

export default useSubstepPersistence;
